import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, ListGroup, Row, Table } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import CashRequestDecisionModal from '../components/CashRequestDecisionModal';
import CashSessionActionModal from '../components/CashSessionActionModal';
import CashHistoryPanel from '../components/CashHistoryPanel';
import CashSessionSummaryTable from '../components/CashSessionSummaryTable';
import cashSessionService from '../services/cashSessionService';
import userService from '../services/userService';

function normalizeRights(val) {
  if (Array.isArray(val)) return val;
  if (!val) return [];

  if (typeof val === 'string') {
    try {
      const parsed = JSON.parse(val);
      if (Array.isArray(parsed)) return parsed;
      if (parsed && typeof parsed === 'object') {
        return Object.keys(parsed).filter((k) => !!parsed[k]);
      }
      return [];
    } catch (_) {
      return val.split(',').map((s) => s.trim()).filter(Boolean);
    }
  }

  if (typeof val === 'object') {
    return Object.keys(val).filter((k) => !!val[k]);
  }

  return [];
}

function toSafeUser(row) {
  if (!row) return null;
  return {
    id: row.id,
    username: row.username,
    rights: normalizeRights(row.rights),
  };
}

function requestLabel(type) {
  if (type === 'OPEN') return 'Ouverture';
  if (type === 'APPORT') return 'Apport';
  if (type === 'RETRAIT') return 'Retrait';
  if (type === 'CLOSE') return 'Clôture';
  return type;
}

function requestBadge(type) {
  if (type === 'OPEN') return 'primary';
  if (type === 'APPORT') return 'success';
  if (type === 'RETRAIT') return 'warning';
  if (type === 'CLOSE') return 'danger';
  return 'secondary';
}

function statusVariant(status) {
  if (status === 'PENDING') return 'warning';
  if (status === 'APPROVED') return 'success';
  if (status === 'REJECTED') return 'secondary';
  return 'light';
}

function buildRequestDetail(row) {
  if (!row) return '—';

  if (row.requestType === 'OPEN') {
    return `Open USD ${Number(row.payload?.openUsd || 0).toFixed(2)} / HTG ${Number(row.payload?.openHtg || 0).toFixed(2)}`;
  }

  if (row.requestType === 'APPORT' || row.requestType === 'RETRAIT') {
    return `${row.currency || ''} ${Number(row.amount || 0).toFixed(2)}`;
  }

  if (row.requestType === 'CLOSE') {
    return [
      `Solde système USD ${Number(row.payload?.systemBalanceUsd || 0).toFixed(2)}`,
      `HTG ${Number(row.payload?.systemBalanceHtg || 0).toFixed(2)}`,
      `| Déclaré par l’utilisateur USD ${Number(row.payload?.requesterRealBalanceUsd || 0).toFixed(2)}`,
      `HTG ${Number(row.payload?.requesterRealBalanceHtg || 0).toFixed(2)}`,
    ].join(' ');
  }

  return '—';
}

export default function CashOpeningManagementPage() {
  const { hasRight } = useContext(AuthContext);

  const canManageSection =
    hasRight('CASH_MANAGEMENT_VIEW') ||
    hasRight('CASH_OPEN') ||
    hasRight('CASH_APPORT') ||
    hasRight('CASH_WITHDRAW') ||
    hasRight('CASH_CLOSE') ||
    hasRight('CASH_APPROVE_OPEN') ||
    hasRight('CASH_APPROVE_APPORT') ||
    hasRight('CASH_APPROVE_WITHDRAW') ||
    hasRight('CASH_APPROVE_CLOSE');

  const canDirectOpen = hasRight('CASH_DIRECT_OPEN') || hasRight('CASH_OPEN');
  const canDirectApport = hasRight('CASH_DIRECT_APPORT') || hasRight('CASH_APPORT');
  const canDirectWithdraw = hasRight('CASH_DIRECT_WITHDRAW') || hasRight('CASH_WITHDRAW');
  const canDirectClose = hasRight('CASH_DIRECT_CLOSE') || hasRight('CASH_CLOSE');

  const canApproveOpen = hasRight('CASH_APPROVE_OPEN') || hasRight('CASH_OPEN');
  const canApproveApport = hasRight('CASH_APPROVE_APPORT') || hasRight('CASH_APPORT');
  const canApproveWithdraw = hasRight('CASH_APPROVE_WITHDRAW') || hasRight('CASH_WITHDRAW');
  const canApproveClose = hasRight('CASH_APPROVE_CLOSE') || hasRight('CASH_CLOSE');

  const canViewUsers = hasRight('USER_VIEW');
  const canHistory = hasRight('CASH_HISTORY_VIEW');
  const canEditOpeningAmounts = hasRight('CASH_OPEN_AMOUNT_EDIT');

  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedUserId, setSelectedUserId] = useState('');
  const [overview, setOverview] = useState(null);

  const [errorMsg, setErrorMsg] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingOverview, setLoadingOverview] = useState(false);

  const [decisionModal, setDecisionModal] = useState({
    show: false,
    mode: 'approve',
    requestItem: null,
  });

  const [actionModal, setActionModal] = useState({
    show: false,
    mode: 'directOpen',
  });

  const selectedUser = useMemo(
    () => users.find((u) => String(u.id) === String(selectedUserId)) || null,
    [users, selectedUserId]
  );

  const filteredUsers = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return users;
    return users.filter((user) =>
      String(user.username || '').toLowerCase().includes(q)
    );
  }, [users, search]);

  const currentSession = overview?.currentSession || null;
  const previousBalance = overview?.previousBalance || { usd: 0, htg: 0 };
  const pendingRequests = overview?.pendingRequests || [];
  const pendingOpen = pendingRequests.find((row) => row.requestType === 'OPEN');

  const currentSessionRequests = useMemo(() => {
    return Array.isArray(currentSession?.requests) ? currentSession.requests : [];
  }, [currentSession]);

  const loadUsers = async () => {
    if (!canManageSection || !canViewUsers) return;

    setLoadingUsers(true);
    setErrorMsg('');

    try {
      const rows = await userService.list();
      const safeUsers = Array.isArray(rows)
        ? rows.map(toSafeUser).filter(Boolean).sort((a, b) => a.username.localeCompare(b.username))
        : [];

      setUsers(safeUsers);

      if (safeUsers.length && !selectedUserId) {
        setSelectedUserId(String(safeUsers[0].id));
      }
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || 'Erreur chargement utilisateurs.');
    } finally {
      setLoadingUsers(false);
    }
  };

  const loadOverview = async (userId) => {
    if (!userId || !canManageSection) return;

    setLoadingOverview(true);
    setErrorMsg('');

    try {
      const payload = await cashSessionService.userOverview(userId);
      setOverview(payload);

      if (payload?.user) {
        const safe = toSafeUser(payload.user);
        setUsers((prev) =>
          prev.map((u) => (String(u.id) === String(safe.id) ? safe : u))
        );
      }
    } catch (e) {
      setOverview(null);
      setErrorMsg(e?.response?.data?.message || 'Erreur chargement caisse utilisateur.');
    } finally {
      setLoadingOverview(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, [canManageSection, canViewUsers]);

  useEffect(() => {
    if (selectedUserId) {
      loadOverview(selectedUserId);
    }
  }, [selectedUserId, canManageSection]);

  const submitDecision = async (values) => {
    const requestItem = decisionModal.requestItem;
    if (!requestItem) return;

    try {
      if (decisionModal.mode === 'reject') {
        await cashSessionService.rejectRequest(requestItem.id, {
          note: values.note || null,
        });
      } else {
        const payload = { note: values.note || null };

        if (requestItem.requestType === 'OPEN') {
          payload.openUsd = values.openUsd ?? requestItem.payload?.openUsd ?? previousBalance.usd ?? 0;
          payload.openHtg = values.openHtg ?? requestItem.payload?.openHtg ?? previousBalance.htg ?? 0;
        }

        if (decisionModal.mode === 'approve-close') {
          payload.realBalanceUsd = values.realBalanceUsd || 0;
          payload.realBalanceHtg = values.realBalanceHtg || 0;
        }

        await cashSessionService.approveRequest(requestItem.id, payload);
      }

      setDecisionModal({ show: false, mode: 'approve', requestItem: null });
      await loadOverview(selectedUserId);
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || 'Erreur traitement demande.');
    }
  };

  const submitDirectAction = async (values) => {
    try {
      if (actionModal.mode === 'directOpen') {
        await cashSessionService.directOpenForUser(selectedUserId, {
          openUsd: values.openUsd || 0,
          openHtg: values.openHtg || 0,
          note: values.note || null,
        });
      } else if (actionModal.mode === 'directApport') {
        await cashSessionService.directApportForUser(selectedUserId, {
          currency: values.currency,
          amount: values.amount,
          note: values.note || null,
        });
      } else if (actionModal.mode === 'directRetrait') {
        await cashSessionService.directRetraitForUser(selectedUserId, {
          currency: values.currency,
          amount: values.amount,
          note: values.note || null,
        });
      } else if (actionModal.mode === 'directClose') {
        await cashSessionService.directCloseForUser(selectedUserId, {
          realBalanceUsd: values.realBalanceUsd || 0,
          realBalanceHtg: values.realBalanceHtg || 0,
          note: values.note || null,
        });
      }

      setActionModal({ show: false, mode: 'directOpen' });
      await loadOverview(selectedUserId);
    } catch (e) {
      setErrorMsg(e?.response?.data?.message || 'Erreur action directe.');
    }
  };

  const canApproveRequest = (row) => {
    if (!row || row.status !== 'PENDING') return false;
    if (row.requestType === 'OPEN') return canApproveOpen;
    if (row.requestType === 'APPORT') return canApproveApport;
    if (row.requestType === 'RETRAIT') return canApproveWithdraw;
    if (row.requestType === 'CLOSE') return canApproveClose;
    return false;
  };

  return (
    <Container className="pt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2>Ouverture des caisses</h2>
        <Button
          variant="outline-secondary"
          onClick={() => {
            loadUsers();
            if (selectedUserId) loadOverview(selectedUserId);
          }}
          disabled={loadingUsers || loadingOverview}
        >
          Actualiser
        </Button>
      </div>

      {(!canManageSection || !canViewUsers) && (
        <Alert variant="warning">
          Accès refusé à la gestion administrative des caisses.
        </Alert>
      )}

      {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

      {canManageSection && canViewUsers && (
        <Row className="g-4">
          <Col lg={4}>
            <Card className="shadow-sm h-100">
              <Card.Body>
                <h5 className="mb-3">Recherche utilisateur</h5>

                <Form.Control
                  placeholder="Rechercher un utilisateur..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="mb-3"
                />

                <ListGroup style={{ maxHeight: 520, overflowY: 'auto' }}>
                  {filteredUsers.map((user) => (
                    <ListGroup.Item
                      key={user.id}
                      action
                      active={String(selectedUserId) === String(user.id)}
                      onClick={() => setSelectedUserId(String(user.id))}
                    >
                      {user.username}
                    </ListGroup.Item>
                  ))}

                  {!filteredUsers.length && (
                    <ListGroup.Item className="text-muted">
                      Aucun utilisateur trouvé.
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={8}>
            <Card className="shadow-sm mb-4">
              <Card.Body>
                <h5 className="mb-1">
                  Utilisateur sélectionné : {selectedUser?.username || '—'}
                </h5>
                <div className="text-muted small">
                  Vue administrative de la caisse unique de l’utilisateur.
                </div>
              </Card.Body>
            </Card>

            {!currentSession && pendingOpen && (
              <Card className="shadow-sm mb-4">
                <Card.Body>
                  <h5 className="mb-3">Demande d’ouverture en attente</h5>
                  <Table hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Détail</th>
                        <th>Note</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{pendingOpen.requestedAt ? new Date(pendingOpen.requestedAt).toLocaleString() : '—'}</td>
                        <td>
                          <Badge bg={requestBadge(pendingOpen.requestType)}>
                            {requestLabel(pendingOpen.requestType)}
                          </Badge>
                        </td>
                        <td>{buildRequestDetail(pendingOpen)}</td>
                        <td>{pendingOpen.requesterNote || '—'}</td>
                        <td>
                          {canApproveRequest(pendingOpen) ? (
                            <div className="d-flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                onClick={() =>
                                  setDecisionModal({
                                    show: true,
                                    mode: 'approve',
                                    requestItem: pendingOpen,
                                  })
                                }
                              >
                                Confirmer
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() =>
                                  setDecisionModal({
                                    show: true,
                                    mode: 'reject',
                                    requestItem: pendingOpen,
                                  })
                                }
                              >
                                Rejeter
                              </Button>
                            </div>
                          ) : (
                            <span className="text-muted small">Sans droit de traitement</span>
                          )}
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            <Card className="shadow-sm mb-4">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3">
                  <div>
                    <h5 className="mb-1">Caisse actuelle</h5>
                    <div className="text-muted small">
                      L’administrateur peut aussi agir directement sans attendre les demandes utilisateur.
                    </div>
                  </div>

                  {currentSession && (
                    <Badge bg={currentSession.status === 'OPEN' ? 'success' : 'warning'}>
                      {currentSession.status}
                    </Badge>
                  )}
                </div>

                {loadingOverview && <div className="text-muted">Chargement...</div>}

                {!loadingOverview && currentSession && (
                  <>
                    <CashSessionSummaryTable
                      session={currentSession}
                      showRequesterClose
                    />

                    <div className="d-flex gap-2 flex-wrap mt-3">
                      {currentSession.status === 'OPEN' && canDirectApport && (
                        <Button
                          variant="outline-success"
                          onClick={() => setActionModal({ show: true, mode: 'directApport' })}
                        >
                          Faire un apport direct
                        </Button>
                      )}

                      {currentSession.status === 'OPEN' && canDirectWithdraw && (
                        <Button
                          variant="outline-warning"
                          onClick={() => setActionModal({ show: true, mode: 'directRetrait' })}
                        >
                          Faire un retrait direct
                        </Button>
                      )}

                      {currentSession.status === 'OPEN' && canDirectClose && (
                        <Button
                          variant="outline-danger"
                          onClick={() => setActionModal({ show: true, mode: 'directClose' })}
                        >
                          Clôturer directement
                        </Button>
                      )}
                    </div>
                  </>
                )}

                {!loadingOverview && !currentSession && (
                  <>
                    <Alert variant="info" className="mb-3">
                      Aucune caisse active ou en attente pour cet utilisateur.
                    </Alert>

                    {canDirectOpen && (
                      <Button
                        onClick={() =>
                          setActionModal({ show: true, mode: 'directOpen' })
                        }
                      >
                        Ouvrir la caisse de {selectedUser?.username || 'cet utilisateur'}
                      </Button>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>

            <Card className="shadow-sm mb-4">
              <Card.Body>
                <h5 className="mb-3">Demandes liées à la session en cours</h5>

                {!currentSession ? (
                  <Alert variant="light" className="mb-0">
                    Aucune session en cours. Cette liste est réinitialisée tant qu’une nouvelle session n’est pas ouverte.
                  </Alert>
                ) : (
                  <Table hover responsive>
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Statut</th>
                        <th>Détail</th>
                        <th>Demandé par</th>
                        <th>Traité par</th>
                        <th>Note</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentSessionRequests.length === 0 ? (
                        <tr>
                          <td colSpan={8} className="text-center text-muted">
                            Aucune demande liée à cette session.
                          </td>
                        </tr>
                      ) : (
                        currentSessionRequests.map((row) => (
                          <tr key={row.id}>
                            <td>{row.requestedAt ? new Date(row.requestedAt).toLocaleString() : '—'}</td>
                            <td>
                              <Badge bg={requestBadge(row.requestType)}>
                                {requestLabel(row.requestType)}
                              </Badge>
                            </td>
                            <td>
                              <Badge bg={statusVariant(row.status)}>
                                {row.status}
                              </Badge>
                            </td>
                            <td>{buildRequestDetail(row)}</td>
                            <td>{row.requestedByUser?.username || currentSession.user?.username || '—'}</td>
                            <td>{row.approvedByUser?.username || row.rejectedByUser?.username || '—'}</td>
                            <td>{row.decisionNote || row.requesterNote || '—'}</td>
                            <td>
                              {row.status === 'PENDING' ? (
                                canApproveRequest(row) ? (
                                  <div className="d-flex gap-2 flex-wrap">
                                    <Button
                                      size="sm"
                                      onClick={() =>
                                        setDecisionModal({
                                          show: true,
                                          mode: row.requestType === 'CLOSE' ? 'approve-close' : 'approve',
                                          requestItem: row,
                                        })
                                      }
                                    >
                                      Confirmer
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline-danger"
                                      onClick={() =>
                                        setDecisionModal({
                                          show: true,
                                          mode: 'reject',
                                          requestItem: row,
                                        })
                                      }
                                    >
                                      Rejeter
                                    </Button>
                                  </div>
                                ) : (
                                  <span className="text-muted small">Sans droit de traitement</span>
                                )
                              ) : (
                                <span className="text-muted small">Traitée</span>
                              )}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                )}
              </Card.Body>
            </Card>

            {canHistory && (
              <CashHistoryPanel
                scope="user"
                userId={selectedUserId}
                title="Historique des sessions clôturées de l’utilisateur"
              />
            )}
          </Col>
        </Row>
      )}

      <CashRequestDecisionModal
        show={decisionModal.show}
        mode={decisionModal.mode}
        requestItem={decisionModal.requestItem}
        session={currentSession}
        previousBalance={previousBalance}
        canEditOpeningAmounts={canEditOpeningAmounts}
        onHide={() =>
          setDecisionModal({ show: false, mode: 'approve', requestItem: null })
        }
        onSubmit={submitDecision}
      />

      <CashSessionActionModal
        show={actionModal.show}
        mode={actionModal.mode}
        session={currentSession}
        previousBalance={previousBalance}
        canEditOpeningAmounts={canEditOpeningAmounts}
        onHide={() => setActionModal({ show: false, mode: 'directOpen' })}
        onSubmit={submitDirectAction}
      />

    </Container>
  );
}
